import { styled } from '@mui/system';
import { Box, Typography, Link, Button, Popover } from '@mui/material';
import { motion } from 'framer-motion';
import {
  SectionHeading,
  SectionSubtext,
  StatCard,
  StatPercentage,
  StatDescription,
} from '../../styles';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fontFamilies } from '../../../../../constants/theme';

export const StatisticsHeading = styled(SectionHeading)(({ theme }) => ({
  letterSpacing: '-.5px',
  marginBottom: '14px',
  maxWidth: '878px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '10px',
  },
}));

export const StatisticsSubtextPrimary = styled(SectionSubtext)(({ theme }) => ({
  marginBottom: '14px',
  fontSize: '22px',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: '10px',
  },
}));

export const StatisticsSubtextSecondary = styled(SectionSubtext)(({ theme }) => ({
  marginBottom: '50px',
  fontSize: '22px',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
    marginBottom: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: '30px',
  },
}));

export const StatisticsGridContainer = styled(Box)(({ theme }) => ({
  marginBottom: '50px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '30px',
  },
}));

export const StatisticsGridWrapper = styled(Box)(({ theme }) => ({
  marginBottom: '50px',
  width: '100%',
  minHeight: '200px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
    minHeight: '180px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '30px',
    minHeight: '150px',
  },
}));

export const StatisticsGridWrapperNew = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '200px',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '14px',
  gridAutoFlow: 'row',

  [theme.breakpoints.up(620)]: {
    gridTemplateColumns: 'repeat(2, 300px)',
    justifyContent: 'center',
  },
  [theme.breakpoints.up(800)]: {
    gridTemplateColumns: 'repeat(2, 300px)',
    justifyContent: 'center',
  },
  [theme.breakpoints.up(950)]: {
    gridTemplateColumns: 'repeat(3, 300px)',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 384px)',
    gap: '24px',
  },
}));

export const StatisticsCardWrapper = styled(StatCard)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '300px',
});

export const CardWrapperBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  maxWidth: '384px',
  width: '100%',
  height: '307px',

  [theme.breakpoints.down('sm')]: {
    position: 'static',
    height: 'auto',
    minHeight: '307px',

    '&>div:first-of-type': {
      width: '100% !important',
      maxWidth: '384px',
    },
  },

  [theme.breakpoints.between('sm', 'lg')]: {
    width: '300px',
  },

  [theme.breakpoints.up('lg')]: {
    width: '384px',
  },
}));

export const LastCardGridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '24px',
  marginTop: '24px',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

export const LastCardWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gridColumn: 'auto',
  minHeight: '307px',

  [theme.breakpoints.up('md')]: {
    gridColumn: '2 / 4',
  },
}));

export const StatisticsCardWrapperNew = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLastCard' && prop !== 'isExpanded',
})(({ theme, isLastCard, isExpanded }) => ({
  borderRadius: '40px',
  boxShadow: isExpanded ? '0 8px 24px rgba(0,0,0,0.10)' : '0 4px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [theme.breakpoints.down('md')]: {
    borderRadius: '28px',
  },

  [theme.breakpoints.down('sm')]: {
    minWidth: '280px',
    minHeight: '280px',
    height: 'auto',
    overflow: 'visible',
    borderRadius: '20px',
  },

  [theme.breakpoints.between('sm', 'md')]: {
    minWidth: '280px',
  },

  ...(isLastCard && {
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  }),
}));

export const StatCardContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme }) => ({
  padding: '15px 26.5px 34px 29.5px',
  display: 'flex',
  flexDirection: 'column',
  height: 'inherit',
  backgroundColor: '#fff',
  borderRadius: '0 0 40px 40px',
  overflowY: 'auto',
  flex: 1,
  transition: 'height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

  [theme.breakpoints.down('md')]: {
    borderRadius: '0 0 28px 28px',
    padding: '12px 20px 28px 20px',
  },

  [theme.breakpoints.down('sm')]: {
    overflowY: 'visible',
    height: 'auto',
    borderRadius: '0 0 20px 20px',
    padding: '10px 16px 24px 16px',
  },
}));

export const StatisticsPercentage = styled(StatPercentage)(({ theme }) => ({
  fontSize: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
  },
}));
export const StatisticsTitle = styled(StatPercentage, {
  shouldForwardProp: (prop) => prop !== 'isExpanded' && prop !== 'isMobile',
})(({ theme, isExpanded, isMobile }) => ({
  fontSize: '18px',
  marginBottom: 0,
  fontWeight: 400,
  lineHeight: '25px',
  whiteSpace: isExpanded && !isMobile ? 'normal' : 'pre-line',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
  },
}));

export const StatisticsDescription = styled(StatDescription)(({ theme }) => ({
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '15px',
  transition: 'all 0.3s ease-in-out',
  fontWeight: 400,
  lineHeight: '24px',
  whiteSpace: 'pre-line',
  marginBottom: '8px',

  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
  },
}));
export const StatisticsSubtitle = styled(StatDescription)(({ theme }) => ({
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '14px',
  opacity: 0.7,
  marginBottom: '10px',
  fontWeight: 400,
}));

export const StatisticsEvidenceLink = styled(StatDescription)(({ theme }) => ({
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '14px',
  lineHeight: '100%',
  marginTop: 'auto',
  marginLeft: 'auto',
  transition: 'all 0.3s ease-in-out',
  color: '#4a7c9e',
  textDecoration: 'underline',
  cursor: 'pointer',
}));

export const StatisticsClosingText = styled(SectionSubtext)(({ theme }) => ({
  maxWidth: '1150px',
  marginBottom: '82px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '60px',
    fontSize: '18px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '40px',
    fontSize: '16px',
  },
}));

export const EvidenceTrigger = styled(Box)({
  position: 'absolute',
  bottom: '30px',
  right: '30px',
  cursor: 'pointer',
  color: '#4a7c9e',
  textDecoration: 'underline',
  fontSize: '14px',
  marginTop: 'auto',
  marginLeft: 'auto',
  transition: 'all 0.3s ease-in-out',
});

export const StyledPopover = styled(Popover)(({ theme }) => ({
  zIndex: 1300,
  '& .MuiPaper-root': {
    width: '480px',
    height: 'auto',
    maxHeight: '80vh',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'visible',
    [theme.breakpoints.down('md')]: {
      width: '90vw',
      maxWidth: '400px',
    },
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
}));

export const PopoverMouseArea = styled(Box)({
  padding: '48px',
  margin: '-48px',
  backgroundColor: 'transparent',
});

export const PopoverContent = styled(Box)({
  height: '100%',
  maxHeight: 'calc(80vh - 48px)',
  overflowY: 'auto',
  position: 'relative',
  backgroundColor: '#e7ecf1',
  padding: '24px',
  borderRadius: '14px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
});

export const PopoverCloseButton = styled(Button)({
  position: 'absolute',
  top: '10px',
  right: '10px',
  minWidth: '13px',
  zIndex: 1000,
});

export const PopoverTitle = styled(Typography)({
  marginBottom: '16px',
  fontWeight: 500,
  fontSize: '13px',
  fontFamily: fontFamilies.gotham,
  color: '#33261d',
  textTransform: 'uppercase',
  opacity: '0.7',
});

export const ReferenceBox = styled(Box)({
  marginBottom: '12px',
});

export const ReferenceLink = styled(Link)({
  color: '#4a7c9e',
  textDecoration: 'underline',
  fontSize: '13px',
  fontFamily: fontFamilies.gotham,
  letterSpacing: '0.7px',
  fontWeight: 400,
  '&:hover': {
    textDecoration: 'none',
  },
});

export const ReferenceIcon = styled(OpenInNewIcon)({
  width: '13px',
  height: '13px',
  marginLeft: '4px',
  marginBottom: '2px',
  alignSelf: 'center',
});

export const ReferenceText = styled(Typography)({
  color: '#33261d',
  fontSize: '13px',
  fontFamily: fontFamilies.gotham,
  fontWeight: 400,
});

export const ReferenceTextItalic = styled(Typography)({
  color: '#33261d',
  fontSize: '13px',
  fontFamily: fontFamilies.gotham,
  fontStyle: 'italic',
  fontWeight: 400,
});

export const DescriptionLink = styled('a')({
  color: '#4a7c9e',
});

export const ReadMoreButton = styled('span')({
  color: '#4a7c9e',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  display: 'contents',
  transition: 'opacity 0.2s ease-in-out',
  '&:hover': {
    opacity: 0.7,
  },
});

export const DescriptionWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
});

export const MotionCardContainer = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ isExpanded, theme }) => ({
  position: 'absolute',
  top: 0,
  width: '100%',
  zIndex: isExpanded ? 10 : 1,
  transition: isExpanded ? 'z-index 0s 0s' : 'z-index 0s 0.4s',

  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 'auto',
    zIndex: 'auto',
  },
}));
